import { cloneDeep, isEqual } from 'lodash';

import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { ItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes';

export const findPath = (
  node: DocumentItem,
  tree: DocumentItem,
): Array<string | number> | null => {
  let found: Array<string | number> | null = null;

  const traverse = (
    node: DocumentItem,
    tree: DocumentItem,
    path: Array<string | number> = [],
  ): Array<string | number> | null => {
    if (isEqual(node, tree)) {
      return path;
    }

    if (tree?.children) {
      tree.children.forEach((child, index) => {
        const p = traverse(node, child, [...path, 'children', index]);

        if (p) {
          found = p;
        }

        return p;
      });
    }

    return found;
  };

  return traverse(node, tree);
};

export const assignPath = (
  tree: DocumentItem,
  node?: DocumentItem,
  paths: { [key: string]: Array<string | number> | null } = {},
): { [key: string]: Array<string | number> | null } => {
  node = node || tree;

  paths[node.id] = findPath(node, tree);

  if (node.children) {
    node.children
      .filter(Boolean)
      .forEach((child) => assignPath(tree, child, paths));
  }

  return paths;
};

export const traverseItem = <T extends ItemAttributes = ItemAttributes>(
  item: DocumentItem<T>,
  updater: (item: DocumentItem<T>) => void,
): DocumentItem<T> => {
  const traverse = (item: DocumentItem<T>): DocumentItem<T> => {
    updater(item);

    if (item.children) {
      // @ts-expect-error untyped
      item.children = item.children.filter(Boolean).map(traverse);
    }

    return item;
  };

  return traverse(item);
};

export const findItem = (
  item: DocumentItem,
  predicate: (item: DocumentItem) => boolean,
): DocumentItem | undefined => {
  let foundItem: DocumentItem | undefined;

  const traverse = (item: DocumentItem): void => {
    const hasMatchItem = predicate(item);

    if (hasMatchItem) {
      foundItem = item;
    }

    if (!foundItem && item.children) {
      item.children.forEach(traverse);
    }
  };

  traverse(item);

  return foundItem;
};

export const cleanItem = (
  item: DocumentItem,
  { removeIds = [] }: { removeIds?: string[] } = {},
): DocumentItem =>
  traverseItem(cloneDeep(item), (item) => {
    const { attributes } = item || {};

    if (removeIds.length && item.children) {
      item.children = item.children.reduce(
        (acc: DocumentItem[], curr: DocumentItem) => {
          if (removeIds.indexOf(curr.id) === -1) {
            return [...acc, curr];
          }

          return acc;
        },
        [],
      );
    }

    if (attributes) {
      Object.keys(attributes).forEach((attributeName) => {
        // Remove every attribute start with "_"
        if (attributeName.startsWith('_')) {
          delete item?.attributes?.[attributeName];
        }
      });
    }
  });

export const getContent = (content?: string | null): string => {
  if (content) {
    return content.replace(/(\r\n|\n|\r)/gm, '');
  }

  return '';
};

export const cleanHtml = (item: DocumentItem): string | null => {
  if (item.type === 'Html') {
    return item.content || null;
  }

  const content = getContent(item?.content);

  if (item?.content) {
    const el = document.createElement('div');

    el.innerHTML = content;

    Array.from(el.children).forEach((child: any) => {
      (child as HTMLElement).style.margin = ''; // Remove some unwanted styles

      // Clean style attribute if we don't have any custom styles
      if ((child as HTMLElement).style.cssText === '') {
        (child as HTMLElement).removeAttribute('style');
      }

      // Remove some unwanted attributes
      (child as HTMLElement).removeAttribute('class');
    });

    return el.innerHTML;
  }

  return null;
};
